import { packingStateSchema, type PackingState, type RemoteShare, type TripShareSettings } from './schema';
import { normalizeReadinessState } from './readiness';
import { apiUrl } from '../platform/api-origin';
import { TOSS_APP_ORIGINS } from '../platform/toss-origin';

type ShareEnvelope = { ciphertext: string; iv: string; expiresAt: string };
type ShareReadResponse = { ok: true; data: ShareEnvelope & { revision: number } };
type ShareCreateResponse = { ok: true; data: { shareId: string; editToken: string; revision: number; expiresAt: string } };

export class ShareApiError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = 'ShareApiError';
  }
}

function base64Url(bytes: Uint8Array) {
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string) {
  const normalized = value.replaceAll('-', '+').replaceAll('_', '/').padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(normalized);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function projectTripForShare(state: PackingState, settings: TripShareSettings): PackingState {
  const normalized = normalizeReadinessState(state);
  if (settings.packingOwner && settings.packingOwner !== 'shared' && !normalized.trip.ownerIds.includes(settings.packingOwner)) throw new ShareApiError('PACKING_OWNER_CHANGED', '담당자가 바뀌었어요. 공유할 사람을 다시 골라주세요.');
  const selected = settings.includePacking ? normalized.items.filter((item) => !settings.packingOwner || item.ownerId === settings.packingOwner) : [];
  const selectedIds = new Set(selected.map((item) => item.id));
  const projected: PackingState = {
    ...normalized,
    trip: {
      ...normalized.trip,
      remoteShare: undefined,
      expenses: settings.includeExpenses ? normalized.trip.expenses : undefined,
      currencyRates: settings.includeExpenses ? normalized.trip.currencyRates : undefined,
      shareSettings: settings,
      packingReview: undefined,
      forgottenItems: undefined,
      itemInstanceIds: selected.map((item) => item.id),
      extractionOrder: normalized.trip.extractionOrder.filter((id) => selectedIds.has(id)),
      scheduleEntries: normalized.trip.scheduleEntries?.map(({ privateNote: _privateNote, ...entry }) => entry),
      travelDocuments: normalized.trip.travelDocuments?.map(({ note: _note, ...document }) => document),
      memories: normalized.trip.memories?.map((memory) => ({
        ...memory,
        photos: settings.includePhotos ? memory.photos : [],
      })),
    },
    // Recipient assignments are local-only; keep the existing external projection.
    items: selected.map(({ recipientId: _recipientId, ...item }) => item),
    placements: normalized.placements.filter((placement) => selectedIds.has(placement.itemInstanceId)),
  };
  if (!settings.includePacking) {
    projected.trip.itemInstanceIds = [];
    projected.trip.extractionOrder = [];
  }
  return packingStateSchema.parse(projected);
}

async function encryptState(state: PackingState, settings: TripShareSettings, encryptionKey?: string): Promise<ShareEnvelope & { encryptionKey: string }> {
  const keyBytes = encryptionKey ? fromBase64Url(encryptionKey) : crypto.getRandomValues(new Uint8Array(32));
  const key = await crypto.subtle.importKey('raw', keyBytes, 'AES-GCM', false, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(projectTripForShare(state, settings)));
  if (encoded.byteLength > 2_000_000) throw new ShareApiError('SHARE_TOO_LARGE', '공유 자료가 2MB를 넘어요. 사진 공유를 끄거나 사진 수를 줄여주세요.');
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const expiresAt = new Date(Date.now() + settings.expiresInDays * 86_400_000).toISOString();
  return { ciphertext: base64Url(new Uint8Array(cipher)), iv: base64Url(iv), encryptionKey: base64Url(keyBytes), expiresAt };
}

async function parseResponse<T extends { ok: true }>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({ ok: false })) as T | { ok: false; error?: { code?: string; message?: string } };
  if (!response.ok || !body.ok) {
    const code = body.ok ? `HTTP_${response.status}` : body.error?.code ?? `HTTP_${response.status}`;
    const fallback = code === 'SHARE_STORAGE_UNAVAILABLE'
      ? '지금은 공유 링크를 만들 수 없어요. 안전한 여행 파일로 공유해주세요.'
      : code === 'SHARE_CONFLICT'
        ? '다른 사람이 먼저 수정했어요. 최신 자료를 다시 불러온 뒤 합쳐주세요.'
        : '공유 링크를 처리하지 못했어요.';
    throw new ShareApiError(code, body.ok ? fallback : body.error?.message || fallback);
  }
  return body;
}

export async function createEncryptedShare(state: PackingState, settings: TripShareSettings, origin = location.origin): Promise<{ remote: RemoteShare; url: string }> {
  if (settings.packingOwner && settings.permission !== 'view') throw new ShareApiError('PARTIAL_SHARE_VIEW_ONLY', '사람별 준비물은 보기 전용으로 공유해주세요.');
  const encrypted = await encryptState(state, settings);
  const response = await fetch(apiUrl('/api/shares'), {
    method: 'POST',
    credentials: 'omit',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ciphertext: encrypted.ciphertext, iv: encrypted.iv, expiresAt: encrypted.expiresAt }),
  });
  const body = await parseResponse<ShareCreateResponse>(response);
  const remote: RemoteShare = {
    shareId: body.data.shareId,
    editToken: body.data.editToken,
    encryptionKey: encrypted.encryptionKey,
    revision: body.data.revision,
    permission: settings.permission,
    expiresAt: body.data.expiresAt,
    lastSyncedAt: new Date().toISOString(),
  };
  return { remote, url: TOSS_APP_ORIGINS.some((value) => value === origin) ? '' : webShareUrl(remote, origin) };
}

export async function updateEncryptedShare(state: PackingState): Promise<RemoteShare> {
  const remote = state.trip.remoteShare;
  if (!remote) throw new ShareApiError('SHARE_NOT_CONNECTED', '연결된 공유 링크가 없어요.');
  const settings = state.trip.shareSettings ?? { permission: remote.permission, includePacking: true, includePhotos: false, expiresInDays: 30 };
  if (settings.packingOwner && remote.permission !== 'view') throw new ShareApiError('PARTIAL_SHARE_VIEW_ONLY', '기존 편집 링크를 끊고 사람별 보기 링크를 새로 만들어주세요.');
  const encrypted = await encryptState(state, settings, remote.encryptionKey);
  const response = await fetch(apiUrl(`/api/shares/${encodeURIComponent(remote.shareId)}`), {
    method: 'PUT',
    credentials: 'omit',
    headers: { 'content-type': 'application/json', 'x-carryonpan-edit-token': remote.editToken, 'if-match': String(remote.revision) },
    body: JSON.stringify({ ciphertext: encrypted.ciphertext, iv: encrypted.iv, expiresAt: remote.expiresAt }),
  });
  const body = await parseResponse<ShareCreateResponse>(response);
  return { ...remote, revision: body.data.revision, lastSyncedAt: new Date().toISOString() };
}

export async function revokeEncryptedShare(state: PackingState): Promise<void> {
  const remote = state.trip.remoteShare;
  if (!remote) throw new ShareApiError('SHARE_NOT_CONNECTED', '연결된 공유 링크가 없어요.');
  const response = await fetch(apiUrl(`/api/shares/${encodeURIComponent(remote.shareId)}`), {
    method: 'DELETE',
    credentials: 'omit',
    headers: { 'x-carryonpan-edit-token': remote.editToken },
  });
  await parseResponse<{ ok: true }>(response);
}

export function parseShareFragment(hash = location.hash) {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return { encryptionKey: params.get('key') ?? '', editToken: params.get('edit') ?? undefined };
}

export async function loadEncryptedShare(shareId: string, encryptionKey: string): Promise<{ state: PackingState; revision: number; expiresAt: string }> {
  if (!encryptionKey) throw new ShareApiError('SHARE_KEY_MISSING', '링크의 암호화 열쇠가 없어요. 받은 링크 전체를 다시 열어주세요.');
  const response = await fetch(apiUrl(`/api/shares/${encodeURIComponent(shareId)}`), { credentials: 'omit' });
  const body = await parseResponse<ShareReadResponse>(response);
  try {
    const key = await crypto.subtle.importKey('raw', fromBase64Url(encryptionKey), 'AES-GCM', false, ['decrypt']);
    const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64Url(body.data.iv) }, key, fromBase64Url(body.data.ciphertext));
    const parsed = packingStateSchema.parse(JSON.parse(new TextDecoder().decode(plaintext)));
    return { state: normalizeReadinessState(parsed), revision: body.data.revision, expiresAt: body.data.expiresAt };
  } catch {
    throw new ShareApiError('SHARE_DECRYPT_FAILED', '이 링크의 암호화 열쇠로 여행 자료를 열 수 없어요.');
  }
}

export function webShareUrl(remote: RemoteShare, origin = location.origin) {
  if (TOSS_APP_ORIGINS.some((value) => value === origin)) throw new Error('앱인토스 공유에는 별도 열기 코드를 사용해요.');
  const fragment = new URLSearchParams({ key: remote.encryptionKey, ...(remote.permission === 'edit' ? { edit: remote.editToken } : {}) });
  return `${origin}/shared/${encodeURIComponent(remote.shareId)}#${fragment}`;
}
export function shareOpeningCode(remote: Pick<RemoteShare, 'shareId' | 'encryptionKey'>) {
  return `CP1.${remote.shareId}.${remote.encryptionKey}`;
}
export function parseShareOpeningCode(value: string, shareId: string): string {
  const parts = value.trim().match(/^CP1\.([A-Za-z0-9_-]{12,120})\.([A-Za-z0-9_-]{43})$/);
  if (!parts || parts[1] !== shareId) throw new ShareApiError('SHARE_CODE_INVALID', '이 여행의 열기 코드를 확인해주세요. 받은 CP1.으로 시작하는 코드만 붙여넣어주세요.');
  return parts[2]!;
}
